import { useMemo, useState } from 'react';
import type { ConsentTemplate, CreateConsentRecord, Service } from '@salonomy/types';
import { Button, Field, Modal, Select, Spinner, TextArea, TextInput } from '@salonomy/ui';
import { useCaptureConsent, useConsentTemplates } from '../../data/consent.js';
import { useServices } from '../../data/services.js';
import { useToast } from '../../lib/toast.js';
import { CONSENT_KIND_LABEL } from './consent-shared.js';

/**
 * A single template form field, best-effort decoded from the template's `fields` schema (typed `unknown`
 * on the contract). We accept an array of descriptors with a stable key + a label + a kind; anything we
 * don't recognise degrades to a text input. A salon that authored no structured fields just gets the
 * free-text "Notes / answers" box below.
 */
interface DecodedField {
  key: string;
  label: string;
  kind: 'text' | 'textarea' | 'boolean';
  required: boolean;
}

function decodeFields(fields: unknown): DecodedField[] {
  if (!Array.isArray(fields)) return [];
  const out: DecodedField[] = [];
  for (const raw of fields) {
    if (!raw || typeof raw !== 'object') continue;
    const f = raw as Record<string, unknown>;
    const key = typeof f.key === 'string' ? f.key : typeof f.name === 'string' ? f.name : null;
    if (!key) continue;
    const label = typeof f.label === 'string' ? f.label : key;
    const t = typeof f.type === 'string' ? f.type : 'text';
    const kind: DecodedField['kind'] =
      t === 'boolean' || t === 'checkbox'
        ? 'boolean'
        : t === 'textarea' || t === 'longtext'
          ? 'textarea'
          : 'text';
    out.push({ key, label, kind, required: f.required === true });
  }
  return out;
}

/**
 * Capture a signed consent record for a client (STAFF-LEVEL). The flow, per the locked design:
 *   1. pick an ACTIVE template (we only ever capture against the live version) — its kind + wording drive
 *      everything; for a patch_test, pick the service it covers (drives the server's validUntil window);
 *   2. fill the template's fields (best-effort) + free-text answers;
 *   3. a TYPED-NAME attestation + an explicit "I confirm the client agreed" checkbox — BOTH required to
 *      arm Capture (the checkbox is the staff member's explicit agreement, mirroring the client portal's
 *      "I agree"); the server stamps signedVia='console_staff', signedByStaffId, signedIp, signedAt and
 *      FREEZES the wording into the record's snapshot.
 *
 * The server is authoritative for the snapshot + validUntil; this modal only collects intent. A record is
 * immutable once signed (a correction is a fresh capture), so there is no edit path.
 */
export function CaptureConsentModal({
  clientId,
  clientName,
  onClose,
}: {
  clientId: string;
  clientName: string;
  onClose: () => void;
}) {
  const { data: templates, isLoading } = useConsentTemplates(true);
  const { data: services } = useServices();
  const capture = useCaptureConsent();
  const toast = useToast();

  const active = useMemo(() => (templates ?? []).filter((t) => t.active), [templates]);
  const [templateId, setTemplateId] = useState('');
  const template: ConsentTemplate | undefined = active.find((t) => t.id === templateId);
  const isPatchTest = template?.kind === 'patch_test';

  const patchTestServices = useMemo(
    () => (services ?? []).filter((s: Service) => s.active && s.requiresPatchTest),
    [services],
  );

  const [serviceId, setServiceId] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, string | boolean>>({});
  const [notes, setNotes] = useState('');
  const [signedName, setSignedName] = useState(clientName);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const decoded = useMemo(() => decodeFields(template?.fields), [template]);

  function setField(key: string, value: string | boolean) {
    setFieldValues((prev) => ({ ...prev, [key]: value }));
  }

  const canSubmit = Boolean(template) && signedName.trim().length > 0 && confirmed && !busy;

  async function submit() {
    if (!template) {
      toast.error('Pick a consent template to capture.');
      return;
    }
    if (!signedName.trim()) {
      toast.error('Enter the client’s name as the typed-name attestation.');
      return;
    }
    if (!confirmed) {
      toast.error('Confirm the client agreed before capturing.');
      return;
    }

    // Assemble the answers blob from the structured fields + any free-text notes. Empty if neither.
    const answers: Record<string, unknown> = { ...fieldValues };
    if (notes.trim()) answers.notes = notes.trim();
    const hasAnswers = Object.keys(answers).length > 0;

    const body: CreateConsentRecord = {
      templateId: template.id,
      kind: template.kind,
      signedName: signedName.trim(),
      signatureKind: 'typed',
      ...(isPatchTest && serviceId ? { serviceId } : {}),
      ...(hasAnswers ? { answers } : {}),
    };

    setBusy(true);
    try {
      await capture.mutateAsync({ clientId, body });
      toast.success('Consent captured');
      onClose();
    } catch {
      toast.error('Could not capture this consent. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Capture consent"
      width={520}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            Capture
          </Button>
        </>
      }
    >
      {isLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
          <Spinner size={16} />
          <span className="muted" style={{ fontSize: 13 }}>
            Loading templates…
          </span>
        </div>
      ) : active.length === 0 ? (
        <div className="muted" style={{ fontSize: 13.5, lineHeight: 1.5, padding: '4px 0' }}>
          There are no active consent templates yet. A manager can create one under Settings →
          Consent forms, then you can capture a client’s agreement here.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Consent form">
            <Select
              value={templateId}
              onChange={(e) => {
                setTemplateId(e.target.value);
                setFieldValues({});
                setServiceId('');
              }}
            >
              <option value="">Choose a form…</option>
              {active.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title} · {CONSENT_KIND_LABEL[t.kind]} (v{t.version})
                </option>
              ))}
            </Select>
          </Field>

          {template ? (
            <>
              {/* The wording the client is agreeing to — shown so the staff member can read it aloud /
                  hand it over. The server freezes this same body into the record's snapshot at capture. */}
              <div className="client-detail__block">
                <span className="slm-stat__label">Wording</span>
                <div
                  style={{
                    fontSize: 13,
                    marginTop: 6,
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    maxHeight: 180,
                    overflowY: 'auto',
                  }}
                >
                  {template.body || (
                    <span className="muted">No wording recorded on this form.</span>
                  )}
                </div>
              </div>

              {isPatchTest ? (
                <Field
                  label="Service this patch test covers"
                  hint="Drives how long the patch test stays valid for booking."
                >
                  <Select value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
                    <option value="">No specific service</option>
                    {patchTestServices.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              ) : null}

              {decoded.map((f) =>
                f.kind === 'boolean' ? (
                  <label
                    key={f.key}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5 }}
                  >
                    <input
                      type="checkbox"
                      checked={fieldValues[f.key] === true}
                      onChange={(e) => setField(f.key, e.target.checked)}
                    />
                    {f.label}
                  </label>
                ) : (
                  <Field key={f.key} label={f.label}>
                    {f.kind === 'textarea' ? (
                      <TextArea
                        value={String(fieldValues[f.key] ?? '')}
                        onChange={(e) => setField(f.key, e.target.value)}
                      />
                    ) : (
                      <TextInput
                        value={String(fieldValues[f.key] ?? '')}
                        onChange={(e) => setField(f.key, e.target.value)}
                      />
                    )}
                  </Field>
                ),
              )}

              <Field
                label="Notes / answers"
                hint="Anything the client declared (allergies, medical)."
              >
                <TextArea value={notes} onChange={(e) => setNotes(e.target.value)} />
              </Field>

              <Field
                label="Typed-name attestation"
                hint="The client’s full name, typed as their signature."
              >
                <TextInput
                  value={signedName}
                  onChange={(e) => setSignedName(e.target.value)}
                  placeholder="e.g. Jane Doe"
                />
              </Field>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13.5 }}>
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <span>
                  I confirm the client agreed to this form and provided the name above as their
                  signature. This is captured as durable, immutable evidence.
                </span>
              </label>
            </>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
